/**
 * `/sales/proformas/<id>/edit` - composer pre-loaded with an existing draft
 * proforma (ADR-0032). Only drafts are editable; finalized proformas are
 * frozen and redirect back to the read-only detail page.
 *
 * @see prototype/sales/invoice-new.html - V1.1 editor baseline reused for proformas.
 * @see docs/adr/0032-proforma-invoices.md - draft/finalized lifecycle.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { hasPermission } from "@flint/api-contract";
import { Alert } from "@flint/ui";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { ProformaComposerPage } from "../../../features/proformas/index.ts";
import { meQueryOptions } from "../../../lib/session.ts";

export const Route = createFileRoute("/(workspace)/sales/proformas_/$proformaId/edit")({
  component: EditProformaRoute,
});

function EditProformaRoute() {
  const { proformaId } = Route.useParams();
  const me = useQuery(meQueryOptions);
  const canCreate = hasPermission(me.data?.company?.permissions ?? [], "invoices.create");

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales" },
        { label: "Proformas", to: "/sales/proformas" },
        { label: "Edit draft" },
      ]}
      viewportClassName="!p-0"
      contentClassName="max-w-none"
    >
      {me.isLoading ? (
        <div className="p-6 text-sm text-muted">Checking proforma permissions...</div>
      ) : canCreate ? (
        <ProformaComposerPage loadProformaId={proformaId} />
      ) : (
        <div className="mx-auto max-w-3xl p-6">
          <Alert
            intent="bad"
            title="You don't have permission to edit proforma drafts"
            description="Ask an Owner or Admin to grant invoice draft access."
          />
        </div>
      )}
    </WorkspaceShell>
  );
}
